/// Reads a caught error to answer one question: is this a plan gate, or a
/// permission denial? Only the former is fixable by paying (see
/// `feature-gate.tsx` and `sidebar-nav.gate.ts`, both callers, and the
/// distinction the shore added `PLAN_UPGRADE_REQUIRED` specifically for).
///
/// The shore also throws this code, with `details: { feature: "max_users",
/// cap, used }`, when an account is at its seat cap (not a product feature
/// at all). Callers read `planUpgradeFeature` generically, no special-casing
/// "max_users" against the product `FeatureKey` union, so the invite screen
/// can offer an upgrade the same way a locked product feature does.
///
/// Reads the flat shape this app's `apiCall` actually throws (`err.code`,
/// `err.details`), the same shape every other `err instanceof ApiError &&
/// err.code === "X"` check in this codebase reads (see
/// `create-feedback-dialog.helpers.ts#isSelfFeedbackError`). `apiCall`
/// unwraps the shore's `{ error: { code, details } }` wire envelope into
/// that flat `ApiError` before anything else can see it, so nothing in this
/// app ever hands a caller the nested shape: there is no second shape to
/// read, and pretending otherwise would test a contract that does not exist.
///
/// Narrows `unknown` with type guards only, never a cast: a predicate that
/// throws while deciding whether something is an error would be its own bug.

const PLAN_UPGRADE_REQUIRED = "PLAN_UPGRADE_REQUIRED";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** True when `err` is a plan-gate denial, false for everything else
 *  (a permission denial, `undefined`, a bare string, or any other shape).
 *  Only a plan gate is worth an upgrade prompt. A type predicate so
 *  `planUpgradeFeature` can read `err.details` below without re-narrowing. */
export function isPlanUpgradeError(
  err: unknown,
): err is { code: "PLAN_UPGRADE_REQUIRED"; details?: unknown } {
  return isRecord(err) && err.code === PLAN_UPGRADE_REQUIRED;
}

/** The gated feature key (a product `FeatureKey` like `"goals"`, or the
 *  seat-cap sentinel `"max_users"`) off a plan-upgrade error, or `null` when
 *  `err` isn't one or carries no readable feature. Never throws. */
export function planUpgradeFeature(err: unknown): string | null {
  if (!isPlanUpgradeError(err)) return null;
  const details = err.details;
  if (!isRecord(details)) return null;
  const feature = details.feature;
  return typeof feature === "string" ? feature : null;
}
